/* eslint-disable no-undef */
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import Modal from 'components/Modal/Modal'
import Box from '@mui/material/Box'
import * as yup from 'yup'
import Input from 'components/Input/Input'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikProps } from 'formik'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { StyledInput } from 'components/Input/Input.styled'
import { CoordinatesSchema } from 'lib/utils'

type EditCoordinatesModalProps = {
  handleCloseModal: () => void
  modal: {
    right: boolean
  }
  setModal: Dispatch<SetStateAction<{ right: boolean }>>
  setIsEditing: Dispatch<SetStateAction<boolean>>
  drawnPath: { lat: number; lng: number }[][] | []
  deleteShapes: (overlays: google.maps.Polygon[] | []) => void
  overlays: google.maps.Polygon[] | []
  setDrawnPath: Dispatch<SetStateAction<{ lat: number; lng: number }[][] | []>>
  deliveryAreaId: number | null
}

export const EditCoordinatesModal = ({
  handleCloseModal,
  setModal,
  modal,
  setIsEditing,
  drawnPath,
  deleteShapes,
  overlays,
  setDrawnPath,
  deliveryAreaId,
}: EditCoordinatesModalProps) => {
  const { t } = useTranslation()
  const { coordinates, setCoordinates, selectedDeliveryArea } =
    useServiceAreas()
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false)
  const formikRef =
    useRef<
      FormikProps<{ polygonCoordinates: string; deliveryAreaLabel: string }>
    >(null)

  const validationSchema = yup.object({
    polygonCoordinates: CoordinatesSchema,
  })

  const handleSubmitPreview = (values: {
    polygonCoordinates: string
    deliveryAreaLabel: string
  }) => {
    setIsLoadingCoordinates(true)
    if (values.polygonCoordinates.search(',') === -1) {
      return
    }
    const coordinatesToArray = values.polygonCoordinates.trim().split('\n')
    const coordinatesToNumber = coordinatesToArray.map((lat) => {
      return lat.split(',').map(Number)
    })
    deleteShapes(overlays)
    setDrawnPath([])
    setCoordinates([
      [
        {
          deliveryAreaGroupId:
            coordinates?.length > 0 && coordinates[0].length > 0
              ? coordinates[0][0].deliveryAreaGroupId
              : selectedDeliveryArea.id,
          area: coordinatesToNumber,
          id: deliveryAreaId as number,
          deliveryAreaGroupName: selectedDeliveryArea.name,
          label: values.deliveryAreaLabel,
        },
      ],
    ])
    setIsLoadingCoordinates(false)
    setIsEditing(true)
    setModal({ right: false })
  }
  useEffect(() => {
    if (formikRef.current) {
      if (drawnPath && drawnPath.length > 0) {
        const drawnPathString = drawnPath[0]
          .map((path) => {
            return [Number(path.lat), Number(path.lng)]
          })
          .join('\n')
          .replaceAll(',', ', ')
        formikRef.current.setFieldValue('polygonCoordinates', drawnPathString)
        formikRef.current.setFieldValue(
          'deliveryAreaLabel',
          coordinates?.[0]?.[0]?.label ?? ''
        )
      } else {
        formikRef.current.setFieldValue(
          'deliveryAreaLabel',
          coordinates?.[0]?.[0]?.label ?? ''
        )
        formikRef.current.setFieldValue(
          'polygonCoordinates',
          coordinates?.length > 0 && coordinates[0].length > 0
            ? coordinates[0][0].area
                .map((coordinate) => {
                  return [Number(coordinate[0]), Number(coordinate[1])]
                })
                .join('\n')
                .replaceAll(',', ', ')
            : ''
        )
      }
    }
  }, [coordinates, drawnPath])

  const initialValues = {
    deliveryAreaLabel: coordinates?.[0]?.[0]?.label ?? '',
    polygonCoordinates:
      coordinates?.length > 0 && coordinates[0].length > 0
        ? coordinates[0][0].area.join('\n').replaceAll(',', ', ')
        : '',
  }
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (polygonCoordinates) => {
        handleSubmitPreview(polygonCoordinates)
      }}>
      {({ isValid, dirty, errors, submitForm }) => (
        <Modal
          showButton
          onButtonClick={submitForm}
          buttonIsDisabled={!isValid || !dirty}
          buttonText={t('preview')}
          isLoadingRequest={isLoadingCoordinates && coordinates?.length > 0}
          setModal={handleCloseModal}
          modal={modal}
          title={t('editDeliveryArea')}
          subtitle={t('serviceArea.newImport-instructions')}>
          <Form>
            <Box>
              <StyledInput
                variant='filled'
                width='100%'
                label={t('deliveryAreaLabel')}
                type='text'
                name='deliveryAreaLabel'
                value={formikRef?.current?.values.deliveryAreaLabel}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  formikRef?.current?.setFieldValue(
                    'deliveryAreaLabel',
                    event.target.value
                  )
                }
              />
              <Input
                errorMessage={errors.polygonCoordinates as string | undefined}
                label={t('serviceArea.inputCoordinates')}
                name='polygonCoordinates'
                type='text'
                multiline
                variant='filled'
                rows={20}
                fullWidth
                width='450px'
              />
            </Box>
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
